import React, { Fragment, useEffect, useState } from 'react';
import { Disclosure, Menu, Transition } from '@headlessui/react';
import { Bars3Icon, XMarkIcon, UserCircleIcon, PlusIcon } from '@heroicons/react/24/outline';
import SupportModal from './SupportModal';
import { useAuth } from '../hooks/useAuth';
import { Link, useLocation, useParams } from 'react-router-dom';
import { clsx } from 'clsx';
import Tooltip from './Tooltip';
import { chatbotApi } from '../services/api';
import { Chatbot } from '../types';
import { useUser } from '../contexts/UserContext';
import { formatFileSize } from './knowledge/format';


interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const { user, signOut } = useAuth();
  const { userProfile, usage, refreshProfile } = useUser();
  const location = useLocation();
  const params = useParams();
  const [chatbot, setChatbot] = useState<Chatbot | null>(null);
  const [chatbotsCount, setChatbotsCount] = useState<number>(0);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);

  // Check if we're in a chatbot-specific route
  const chatbotId = params.chatbotId;
  const isInChatbot = !!chatbotId;
  const isOnDashboard = location.pathname === '/dashboard' || location.pathname === '/';
  const isOnSubscription = location.pathname === '/subscription';
  const isOnProfile = location.pathname === '/profile';

  // A grandfathered allowance means this account is being held ABOVE its plan's
  // line because it was already past it — its content keeps working but it
  // cannot add more. That reads very differently from simply being nearly full,
  // so it gets its own wording rather than an amber number.
  const isOverPlanCap = !!usage?.storage.grandfatheredBytes;
  const storageIsTight =
    !!usage && usage.storage.capBytes > 0 &&
    usage.storage.usedBytes / usage.storage.capBytes >= 0.8;


  
  // Load chatbot data when in chatbot context
  useEffect(() => {
    if (chatbotId) {
      chatbotApi.getChatbot(chatbotId).then(response => {
        if (response.success && response.data) {
          setChatbot(response.data);
        }
      }).catch(console.error);
    } else {
      setChatbot(null);
    }
  }, [chatbotId]);

  // Refresh user profile on navigation (keeps balance in sync)
  useEffect(() => {
    refreshProfile();
  }, [location.pathname, refreshProfile]);

  // Load chatbots count for nav stats
  useEffect(() => {
    chatbotApi.getChatbots().then(response => {
      if (response.success && response.data) {
        setChatbotsCount(response.data.length);
      }
    }).catch(console.error);
  }, []);

  const handleSignOut = async () => {
    await signOut();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100 dark:from-gray-900 dark:to-slate-900">
      <Disclosure as="nav" className="bg-gradient-to-r from-slate-700 to-slate-800 backdrop-blur-sm shadow-lg border-b border-slate-600/50 relative z-40">
        {({ open }) => (
          <>
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="flex h-16 justify-between">
                <div className="flex">
                  <div className="flex flex-shrink-0 items-center">
                    <Link
                      to="/dashboard"
                      className="group"
                      title="Go to Dashboard"
                    >
                      <span className={clsx(
                        "text-2xl font-bold transition-colors duration-200",
                        (isInChatbot || isOnSubscription || isOnProfile)
                          ? "text-slate-300 group-hover:text-white"
                          : "text-white"
                      )}>
                        Celsian AI
                      </span>
                    </Link>
                    {/* Breadcrumb separator + chatbot name */}
                    {isInChatbot && chatbot && (
                      <div className="hidden sm:flex sm:items-center">
                        <span className="text-slate-400 mx-3 text-lg">/</span>
                        <span className="text-xl font-bold text-white">
                          {chatbot.name}
                        </span>
                        <div className={clsx(
                          'w-2 h-2 rounded-full ml-2',
                          chatbot.status === 'active' ? 'bg-green-400' :
                          chatbot.status === 'creating' ? 'bg-amber-400 animate-pulse' :
                          chatbot.status === 'failed' ? 'bg-red-400' :
                          'bg-gray-400'
                        )}></div>
                      </div>
                    )}
                  </div>
                  
                  {/* Show subscription title if on subscription page */}
                  {isOnSubscription && (
                    <div className="hidden sm:flex sm:items-center">
                      <span className="text-slate-400 mx-3 text-lg">/</span>
                      <span className="text-xl font-bold text-white">
                        Subscription
                      </span>
                    </div>
                  )}

                  {/* Show profile title if on profile page */}
                  {isOnProfile && (
                    <div className="hidden sm:flex sm:items-center">
                      <span className="text-slate-400 mx-3 text-lg">/</span>
                      <span className="text-xl font-bold text-white">
                        Profile
                      </span>
                    </div>
                  )}
                  
                </div>
                <div className="hidden sm:ml-6 sm:flex sm:items-center sm:space-x-4">
                  {/* Stats - elegant display */}
                  <div className="hidden sm:flex items-center gap-4 text-sm mr-2">
                    <div className="flex items-center gap-1.5">
                      <span className="text-white font-medium">{Math.round(userProfile?.balance || 0).toLocaleString()}</span>
                      <span className="text-slate-300 text-xs">credits</span>
                    </div>
                    {/* Storage sits beside credits because this is where someone
                        looks to find out what they have left. The cap is per
                        ACCOUNT, so this total is the number that actually binds —
                        a per-chatbot bar would imply each bot has its own
                        allowance. Rendered only when usage loaded; a failed
                        fetch shows nothing rather than a wrong allowance. */}
                    {usage && (
                      <>
                        <div className="w-px h-3 bg-slate-500"></div>
                        <Tooltip
                          content={
                            isOverPlanCap
                              ? `${formatFileSize(usage.storage.usedBytes)} stored. This is above the ${formatFileSize(usage.storage.planCapBytes ?? usage.storage.capBytes)} included with ${usage.plan}, so your existing content keeps working — but you cannot add more until you free some space.`
                              : `${formatFileSize(usage.storage.remainingBytes)} of content storage left on your ${usage.plan} plan, shared across all your chatbots.`
                          }
                          position="bottom"
                        >
                          <div className="flex items-center gap-1.5 cursor-help">
                            <span
                              className={clsx(
                                'font-medium',
                                storageIsTight ? 'text-amber-300' : 'text-white'
                              )}
                            >
                              {formatFileSize(usage.storage.usedBytes)}
                            </span>
                            <span className="text-slate-300 text-xs">
                              of {formatFileSize(usage.storage.capBytes)}
                            </span>
                          </div>
                        </Tooltip>
                      </>
                    )}
                    <div className="w-px h-3 bg-slate-500"></div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-white font-medium capitalize">{userProfile?.plan || 'Free'}</span>
                    </div>
                  </div>

                  {/* Support lives in the user menu below, next to Profile and
                      Subscription — it is an occasional errand and does not
                      belong beside the primary New Chatbot action. */}

                  {/* New Chatbot Button - show only on dashboard */}
                  {isOnDashboard && (
                    <Link
                      to="/chatbots/new"
                      className="inline-flex items-center px-4 py-2 border border-slate-400/50 text-sm font-medium rounded-lg text-white bg-slate-600/30 hover:bg-slate-600/50 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-slate-700 transition-all duration-200"
                    >
                      <PlusIcon className="h-4 w-4 mr-2" />
                      New Chatbot
                    </Link>
                  )}

                  <Menu as="div" className="relative ml-3 z-50">
                    <div>
                      <Menu.Button className="flex rounded-full bg-slate-600 text-sm focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-slate-700">
                        <span className="sr-only">Open user menu</span>
                        <UserCircleIcon className="h-8 w-8 text-slate-200" />
                      </Menu.Button>
                    </div>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-200"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute right-0 z-50 mt-2 w-48 origin-top-right rounded-md bg-white dark:bg-gray-800 py-1 shadow-lg ring-1 ring-black dark:ring-gray-600 ring-opacity-5 focus:outline-none">
                        <div className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-600">
                          <Tooltip content={userProfile?.email || user?.email || ''} position="bottom" className="block">
                            <div className="truncate cursor-help">
                              {userProfile?.email || user?.email}
                            </div>
                          </Tooltip>
                        </div>
                        <Menu.Item>
                          {({ active }) => (
                            <Link
                              to="/profile"
                              className={clsx(
                                active ? 'bg-gray-100 dark:bg-gray-700' : '',
                                'block px-4 py-2 text-sm text-gray-700 dark:text-gray-300'
                              )}
                            >
                              Profile
                            </Link>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <Link
                              to="/subscription"
                              className={clsx(
                                active ? 'bg-gray-100 dark:bg-gray-700' : '',
                                'block px-4 py-2 text-sm text-gray-700 dark:text-gray-300'
                              )}
                            >
                              Subscription
                            </Link>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={() => setIsSupportModalOpen(true)}
                              className={clsx(
                                active ? 'bg-gray-100 dark:bg-gray-700' : '',
                                'block w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300'
                              )}
                            >
                              Support
                            </button>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={handleSignOut}
                              className={clsx(
                                active ? 'bg-gray-100 dark:bg-gray-700' : '',
                                'block w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 border-t border-gray-200 dark:border-gray-600'
                              )}
                            >
                              Sign out
                            </button>
                          )}
                        </Menu.Item>
                      </Menu.Items>
                    </Transition>
                  </Menu>
                </div>
                <div className="-mr-2 flex items-center sm:hidden">
                  <Disclosure.Button className="inline-flex items-center justify-center rounded-md bg-slate-600 p-2 text-slate-200 hover:bg-slate-500 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-slate-700">
                    <span className="sr-only">Open main menu</span>
                    {open ? (
                      <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                    ) : (
                      <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                    )}
                  </Disclosure.Button>
                </div>
              </div>
            </div>

            <Disclosure.Panel className="sm:hidden">
              {/* New Chatbot is the only action left up here, so the whole
                  block is conditional — otherwise an empty padded div sits
                  above the account list on every other page. */}
              {isOnDashboard && (
                <div className="px-4 py-2">
                  <Link
                    to="/chatbots/new"
                    className="flex w-full items-center justify-center px-4 py-2 border border-slate-400/50 text-sm font-medium rounded-lg text-white bg-slate-600/30 hover:bg-slate-600/50 hover:border-slate-300"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    New Chatbot
                  </Link>
                </div>
              )}
              <div className="border-t border-gray-200 dark:border-gray-700 pb-3 pt-4">
                <div className="flex items-center px-4">
                  <div className="flex-shrink-0">
                    <UserCircleIcon className="h-10 w-10 text-gray-400 dark:text-gray-300" />
                  </div>
                  <div className="ml-3">
                    <Tooltip content={user?.name || 'User'} position="right" className="block">
                      <div className="text-base font-medium text-gray-800 dark:text-gray-200 truncate cursor-help">{user?.name || 'User'}</div>
                    </Tooltip>
                    <Tooltip content={userProfile?.email || user?.email || ''} position="right" className="block">
                      <div className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate cursor-help">{userProfile?.email || user?.email}</div>
                    </Tooltip>
                  </div>
                </div>
                <div className="mt-3 space-y-1">
                  <Disclosure.Button
                    as={Link}
                    to="/profile"
                    className="block px-4 py-2 text-base font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-800 dark:hover:text-gray-200"
                  >
                    Profile
                  </Disclosure.Button>
                  <Disclosure.Button
                    as={Link}
                    to="/subscription"
                    className="block px-4 py-2 text-base font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-800 dark:hover:text-gray-200"
                  >
                    Subscription
                  </Disclosure.Button>
                  <Disclosure.Button
                    as="button"
                    onClick={() => setIsSupportModalOpen(true)}
                    className="block w-full px-4 py-2 text-left text-base font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-800 dark:hover:text-gray-200"
                  >
                    Support
                  </Disclosure.Button>
                  <Disclosure.Button
                    as="button"
                    onClick={handleSignOut}
                    className="block w-full px-4 py-2 text-left text-base font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-800 dark:hover:text-gray-200"
                  >
                    Sign out
                  </Disclosure.Button>
                </div>
              </div>
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>

      <main className="py-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="animate-in fade-in duration-300">
            {children}
          </div>
        </div>
      </main>

      {/* Support Modal */}
      <SupportModal
        isOpen={isSupportModalOpen}
        onClose={() => setIsSupportModalOpen(false)}
        userEmail={userProfile?.email || user?.email || ''}
        userId={user?.id || ''}
      />
    </div>
  );
}